import React from 'react'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '../../lib/ui/form'
import { Input } from '../../lib/ui/input'
import { cn } from '../../lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/ui/select'
import { Checkbox } from '../../lib/ui/checkbox'
import { Textarea } from '../../lib/ui/textarea'
import { MultiSelect } from '../../lib/ui/multiselect'
import { Button } from '~/lib/ui/button'

export const InputField = ({
  control,
  label,
  description,
  name,
  className,
  inputClassName,
  ...props
}) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormDescription>{description}</FormDescription>
          <FormControl>
            <Input
              {...field}
              {...props}
              className={inputClassName}
              onChange={(e) => {
                if (props.type === 'number') {
                  field.onChange(
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                } else {
                  field.onChange(e.target.value)
                }
              }}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function SelectField({
  name,
  label,
  control,
  className,
  choices,
  description,
  descriptionClassName,
  placeholder,
}) {
  if (!choices) return null
  if (choices.length === 0) return null
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(className)}>
          {label && <FormLabel>{label}</FormLabel>}
          {description && (
            <FormDescription className={cn('text-xs', descriptionClassName)}>
              {description}
            </FormDescription>
          )}
          <Select onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder={placeholder || 'Select one'} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {choices.map((choice, idx) => (
                <SelectItem
                  key={`${name}-${choice.value}-${idx}`}
                  value={choice.value}
                >
                  {choice.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function TextField({
  description,
  placeholder,
  name,
  label,
  control,
  className,
  innerClassName,
  onClick,
  icon: Icon,
  ...props
}) {
  if (Icon) {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem className={className}>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div className="relative">
                <div className="absolute right-0 top-0 z-10 text-muted-foreground">
                  <Button
                    className="px-4 hover:bg-transparent"
                    type="button"
                    variant="ghost"
                    onClick={onClick}
                  >
                    <Icon />
                  </Button>
                </div>
                <Textarea
                  placeholder={placeholder}
                  className={cn('resize-none', innerClassName)}
                  {...field}
                  {...props}
                />
              </div>
            </FormControl>
            <FormDescription>{description}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    )
  }
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea
              placeholder={placeholder}
              className={cn('resize-none', innerClassName)}
              {...field}
              {...props}
            />
          </FormControl>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function CheckboxField({ name, label, control, className }) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(className)}>
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              className="data-[state=checked]:bg-intoo-blue-medium"
            />
          </FormControl>
          <div className="space-y-1 leading-none">
            <FormLabel>{label}</FormLabel>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function InlineCheckboxField({ name, label, control }) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex items-center">
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              className="data-[state=checked]:bg-intoo-blue-medium"
            />
          </FormControl>
          <div className="space-y-1 leading-none">
            <FormLabel className="mb-0 ml-2">{label}</FormLabel>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function MultiSelectField({
  name,
  label,
  control,
  className,
  choices,
  description,
  descriptionClassName,
  placeholder,
}) {
  if (!choices) return null
  if (choices.length === 0) return null
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(className)}>
          {label && <FormLabel>{label}</FormLabel>}
          {description && (
            <FormDescription className={cn('text-xs', descriptionClassName)}>
              {description}
            </FormDescription>
          )}
          <FormControl>
            <MultiSelect
              options={choices}
              value={
                Array.isArray(field.value)
                  ? field.value
                  : field.value
                    ? [field.value]
                    : []
              }
              onChange={field.onChange}
              placeholder={placeholder || 'Select one or more'}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
export function PhoneField({
  control,
  label,
  description,
  name,
  className,
  inputClassName,
  ...props
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormDescription>{description}</FormDescription>
          <FormControl>
            <Input
              {...field}
              {...props}
              className={inputClassName}
              type="tel"
              value={(() => {
                let digits = (field.value || '').replace(/\D/g, '').slice(0, 10)
                if (digits.length > 6) {
                  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`
                } else if (digits.length > 3) {
                  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}`
                }
                return digits
              })()}
              onChange={(e) => {
                let digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                let formatted = digits
                if (digits.length > 6) {
                  formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`
                } else if (digits.length > 3) {
                  formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}`
                }
                field.onChange(formatted)
              }}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
